import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Form, Spinner } from "react-bootstrap";
import { FaPlus, FaSave, FaTrash } from "react-icons/fa";
import { toast } from "react-toastify";
import { homeContentApi } from "../../api/services";
import { getErrorMessage } from "../../api/client";
import CmsImageField from "../../components/CmsImageField";
import FeaturedProductPicker from "../../components/FeaturedProductPicker";
import { FormPageSkeleton } from "../../components/Skeleton";
import "./AdminHomeContent.css";

const TABS = [
  { key: "hero", label: "Hero" },
  { key: "shopCategories", label: "Shop by Category" },
  { key: "featuredProducts", label: "Featured Products" },
  { key: "carousel", label: "Carousel" },
  { key: "industries", label: "Industries" },
  { key: "brandPartners", label: "Brand Partners" },
];

const isHomeUploadUrl = (url) =>
  typeof url === "string" && url.startsWith("/uploads/home/");

const isBlobUrl = (url) => typeof url === "string" && url.startsWith("blob:");

const cloneContent = (value) => JSON.parse(JSON.stringify(value));

const collectHomeImageUrls = (section) => {
  if (!section) return [];
  const urls = [];
  if (isHomeUploadUrl(section.backgroundImage)) {
    urls.push(section.backgroundImage);
  }
  (section.items || []).forEach((item) => {
    if (isHomeUploadUrl(item?.image)) urls.push(item.image);
  });
  return urls;
};

const emptyCategoryItem = () => ({
  title: "",
  image: "",
  link: "/products",
  accentColor: "#ffb347",
  sortOrder: 0,
  isActive: true,
});

const emptyCarouselItem = () => ({
  image: "",
  buttonText: "Shop Now",
  link: "/products",
  sortOrder: 0,
  isActive: true,
});

const emptyIndustryItem = () => ({
  title: "",
  description: "",
  image: "",
  sortOrder: 0,
  isActive: true,
});

const emptyBrandItem = () => ({
  image: "",
  alt: "",
  link: "",
  sortOrder: 0,
  isActive: true,
});

const AdminHomeContent = () => {
  const [activeTab, setActiveTab] = useState("hero");
  const [content, setContent] = useState(null);
  const [savedContent, setSavedContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const pendingFilesRef = useRef(new Map());

  const setPendingFile = useCallback((fieldKey, file) => {
    if (file) pendingFilesRef.current.set(fieldKey, file);
    else pendingFilesRef.current.delete(fieldKey);
  }, []);

  const clearPendingKeysForSection = useCallback((sectionKey) => {
    const map = pendingFilesRef.current;
    [...map.keys()]
      .filter((key) => key === sectionKey || key.startsWith(`${sectionKey}.`))
      .forEach((key) => map.delete(key));
  }, []);

  const reindexItemPending = useCallback((sectionKey, removedIndex) => {
    const map = pendingFilesRef.current;
    const prefix = `${sectionKey}.items.`;
    const suffix = ".image";
    map.delete(`${prefix}${removedIndex}${suffix}`);

    const higher = [...map.entries()]
      .map(([key, file]) => {
        if (!key.startsWith(prefix) || !key.endsWith(suffix)) return null;
        const index = Number(key.slice(prefix.length, -suffix.length));
        if (Number.isNaN(index) || index <= removedIndex) return null;
        return { index, file, key };
      })
      .filter(Boolean)
      .sort((a, b) => a.index - b.index);

    higher.forEach(({ key }) => map.delete(key));
    higher.forEach(({ index, file }) => {
      map.set(`${prefix}${index - 1}${suffix}`, file);
    });
  }, []);

  const uploadPendingForSection = useCallback(async (sectionKey, sectionData) => {
    const data = {
      ...sectionData,
      items: sectionData.items ? sectionData.items.map((item) => ({ ...item })) : sectionData.items,
    };
    const pending = pendingFilesRef.current;

    if (sectionKey === "hero" && pending.has("hero.backgroundImage")) {
      const res = await homeContentApi.uploadImages([pending.get("hero.backgroundImage")]);
      const url = res.data.data.urls?.[0] || "";
      if (isBlobUrl(data.backgroundImage)) URL.revokeObjectURL(data.backgroundImage);
      data.backgroundImage = url;
      pending.delete("hero.backgroundImage");
    } else if (isBlobUrl(data.backgroundImage)) {
      URL.revokeObjectURL(data.backgroundImage);
      data.backgroundImage = "";
    }

    if (Array.isArray(data.items)) {
      for (let i = 0; i < data.items.length; i += 1) {
        const fieldKey = `${sectionKey}.items.${i}.image`;
        if (pending.has(fieldKey)) {
          const res = await homeContentApi.uploadImages([pending.get(fieldKey)]);
          const url = res.data.data.urls?.[0] || "";
          if (isBlobUrl(data.items[i].image)) URL.revokeObjectURL(data.items[i].image);
          data.items[i].image = url;
          pending.delete(fieldKey);
        } else if (isBlobUrl(data.items[i].image)) {
          URL.revokeObjectURL(data.items[i].image);
          data.items[i].image = "";
        }
      }
    }

    return data;
  }, []);

  const deleteReplacedHomeImages = useCallback(async (previousSection, nextSection) => {
    const previous = new Set(collectHomeImageUrls(previousSection));
    const next = new Set(collectHomeImageUrls(nextSection));
    const removals = [...previous].filter((url) => !next.has(url));
    await Promise.all(
      removals.map(async (url) => {
        try {
          await homeContentApi.deleteImage(url);
        } catch {
          // ignore cleanup failures
        }
      })
    );
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const homeRes = await homeContentApi.get();
      const loaded = homeRes.data.data.content;
      setContent(loaded);
      setSavedContent(cloneContent(loaded));
      pendingFilesRef.current.clear();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to load home page content"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const patch = (section, value) => {
    setContent((prev) => ({ ...prev, [section]: value }));
  };

  const saveSection = async (sectionKey) => {
    if (!content) return;
    setSaving(true);
    try {
      let sectionData = { ...content[sectionKey] };
      if (sectionKey === "featuredProducts") {
        delete sectionData.products;
      } else {
        sectionData = await uploadPendingForSection(sectionKey, sectionData);
      }

      const previousSection = savedContent?.[sectionKey];
      const res = await homeContentApi.update({ [sectionKey]: sectionData });
      const nextContent = res.data.data.content;
      await deleteReplacedHomeImages(previousSection, nextContent[sectionKey]);
      clearPendingKeysForSection(sectionKey);
      setContent(nextContent);
      setSavedContent(cloneContent(nextContent));
      toast.success("Section saved");
    } catch (error) {
      toast.error(getErrorMessage(error, "Could not save"));
    } finally {
      setSaving(false);
    }
  };

  const updateItem = (sectionKey, index, field, value) => {
    const section = content[sectionKey];
    const items = [...(section.items || [])];
    items[index] = { ...items[index], [field]: value };
    patch(sectionKey, { ...section, items });
  };

  const addItem = (sectionKey, factory) => {
    const section = content[sectionKey];
    const items = [...(section.items || []), factory()];
    items.forEach((item, i) => {
      item.sortOrder = i;
    });
    patch(sectionKey, { ...section, items });
  };

  const removeItem = (sectionKey, index) => {
    const section = content[sectionKey];
    const removed = section.items?.[index];
    if (isBlobUrl(removed?.image)) {
      URL.revokeObjectURL(removed.image);
    }
    reindexItemPending(sectionKey, index);
    const items = (section.items || []).filter((_, i) => i !== index);
    patch(sectionKey, { ...section, items });
    toast.info("Item removed — click Save to apply");
  };

  if (loading || !content) {
    return (
      <div className="manage-page">
        <FormPageSkeleton />
      </div>
    );
  }

  return (
    <div className="manage-page">
      <header className="manage-head">
        <div>
          <h1>Home page</h1>
          <p>Edit hero, categories, carousel, industries, and brand partners.</p>
        </div>
      </header>

      <div className="home-cms-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`home-cms-tab ${activeTab === tab.key ? "active" : ""}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="home-cms-card">
        {activeTab === "hero" && (
          <>
            <CmsImageField
              label="Background image"
              value={content.hero.backgroundImage}
              onChange={(v) => patch("hero", { ...content.hero, backgroundImage: v })}
              onPendingFile={(file) => setPendingFile("hero.backgroundImage", file)}
            />
            <Form.Group className="mb-3">
              <Form.Label>Badge text</Form.Label>
              <Form.Control
                value={content.hero.badge || ""}
                onChange={(e) => patch("hero", { ...content.hero, badge: e.target.value })}
              />
            </Form.Group>
            <div className="row">
              <div className="col-md-6 mb-3">
                <Form.Label>Title line 1</Form.Label>
                <Form.Control
                  value={content.hero.title || ""}
                  onChange={(e) => patch("hero", { ...content.hero, title: e.target.value })}
                />
              </div>
              <div className="col-md-6 mb-3">
                <Form.Label>Title line 2</Form.Label>
                <Form.Control
                  value={content.hero.titleLine2 || ""}
                  onChange={(e) =>
                    patch("hero", { ...content.hero, titleLine2: e.target.value })
                  }
                />
              </div>
            </div>
            <Form.Group className="mb-3">
              <Form.Label>Tagline</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={content.hero.tagline || ""}
                onChange={(e) => patch("hero", { ...content.hero, tagline: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Features (one per line)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={(content.hero.features || []).join("\n")}
                onChange={(e) =>
                  patch("hero", {
                    ...content.hero,
                    features: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean),
                  })
                }
              />
            </Form.Group>
            <div className="row">
              <div className="col-md-6 mb-3">
                <Form.Label>Primary button text</Form.Label>
                <Form.Control
                  value={content.hero.primaryButtonText || ""}
                  onChange={(e) =>
                    patch("hero", { ...content.hero, primaryButtonText: e.target.value })
                  }
                />
              </div>
              <div className="col-md-6 mb-3">
                <Form.Label>Primary button link</Form.Label>
                <Form.Control
                  value={content.hero.primaryButtonLink || ""}
                  onChange={(e) =>
                    patch("hero", { ...content.hero, primaryButtonLink: e.target.value })
                  }
                />
              </div>
              <div className="col-md-6 mb-3">
                <Form.Label>Secondary button text</Form.Label>
                <Form.Control
                  value={content.hero.secondaryButtonText || ""}
                  onChange={(e) =>
                    patch("hero", { ...content.hero, secondaryButtonText: e.target.value })
                  }
                />
              </div>
              <div className="col-md-6 mb-3">
                <Form.Label>Secondary button link</Form.Label>
                <Form.Control
                  value={content.hero.secondaryButtonLink || ""}
                  onChange={(e) =>
                    patch("hero", { ...content.hero, secondaryButtonLink: e.target.value })
                  }
                />
              </div>
            </div>
            <Form.Group className="mb-3">
              <Form.Label>Footer line</Form.Label>
              <Form.Control
                value={content.hero.footerLine || ""}
                onChange={(e) =>
                  patch("hero", { ...content.hero, footerLine: e.target.value })
                }
              />
            </Form.Group>
            <div className="home-cms-actions">
              <Button variant="dark" disabled={saving} onClick={() => saveSection("hero")}>
                {saving ? <Spinner size="sm" animation="border" /> : <FaSave />} Save hero
              </Button>
            </div>
          </>
        )}

        {activeTab === "shopCategories" && (
          <>
            <div className="row mb-3">
              <div className="col-md-4">
                <Form.Label>Subtitle</Form.Label>
                <Form.Control
                  value={content.shopCategories.subtitle || ""}
                  onChange={(e) =>
                    patch("shopCategories", {
                      ...content.shopCategories,
                      subtitle: e.target.value,
                    })
                  }
                />
              </div>
              <div className="col-md-8">
                <Form.Label>Title</Form.Label>
                <Form.Control
                  value={content.shopCategories.title || ""}
                  onChange={(e) =>
                    patch("shopCategories", {
                      ...content.shopCategories,
                      title: e.target.value,
                    })
                  }
                />
              </div>
            </div>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={content.shopCategories.description || ""}
                onChange={(e) =>
                  patch("shopCategories", {
                    ...content.shopCategories,
                    description: e.target.value,
                  })
                }
              />
            </Form.Group>

            {(content.shopCategories.items || []).map((item, index) => (
              <div className="home-cms-item" key={index}>
                <div className="home-cms-item-head">
                  <h4>Category {index + 1}</h4>
                  <Button
                    size="sm"
                    variant="outline-danger"
                    onClick={() => removeItem("shopCategories", index)}
                  >
                    <FaTrash />
                  </Button>
                </div>
                <div className="row">
                  <div className="col-md-6 mb-2">
                    <Form.Label>Title</Form.Label>
                    <Form.Control
                      value={item.title || ""}
                      onChange={(e) =>
                        updateItem("shopCategories", index, "title", e.target.value)
                      }
                    />
                  </div>
                  <div className="col-md-6 mb-2">
                    <Form.Label>Link</Form.Label>
                    <Form.Control
                      value={item.link || ""}
                      onChange={(e) =>
                        updateItem("shopCategories", index, "link", e.target.value)
                      }
                      placeholder="/products?category=..."
                    />
                  </div>
                  <div className="col-md-6 mb-2">
                    <Form.Label>Accent color</Form.Label>
                    <Form.Control
                      type="color"
                      value={item.accentColor || "#ffb347"}
                      onChange={(e) =>
                        updateItem("shopCategories", index, "accentColor", e.target.value)
                      }
                    />
                  </div>
                  <div className="col-12">
                    <CmsImageField
                      label="Category image"
                      value={item.image}
                      onChange={(v) => updateItem("shopCategories", index, "image", v)}
                      onPendingFile={(file) =>
                        setPendingFile(`shopCategories.items.${index}.image`, file)
                      }
                    />
                  </div>
                </div>
              </div>
            ))}

            <Button
              variant="outline-dark"
              className="mb-3"
              onClick={() => addItem("shopCategories", emptyCategoryItem)}
            >
              <FaPlus /> Add category card
            </Button>

            <div className="home-cms-actions">
              <Button
                variant="dark"
                disabled={saving}
                onClick={() => saveSection("shopCategories")}
              >
                {saving ? <Spinner size="sm" animation="border" /> : <FaSave />} Save categories
              </Button>
            </div>
          </>
        )}

        {activeTab === "featuredProducts" && (
          <>
            <div className="row mb-3">
              <div className="col-md-4">
                <Form.Label>Subtitle</Form.Label>
                <Form.Control
                  value={content.featuredProducts?.subtitle || ""}
                  onChange={(e) =>
                    patch("featuredProducts", {
                      ...content.featuredProducts,
                      subtitle: e.target.value,
                    })
                  }
                />
              </div>
              <div className="col-md-8">
                <Form.Label>Title</Form.Label>
                <Form.Control
                  value={content.featuredProducts?.title || ""}
                  onChange={(e) =>
                    patch("featuredProducts", {
                      ...content.featuredProducts,
                      title: e.target.value,
                    })
                  }
                />
              </div>
            </div>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={content.featuredProducts?.description || ""}
                onChange={(e) =>
                  patch("featuredProducts", {
                    ...content.featuredProducts,
                    description: e.target.value,
                  })
                }
              />
            </Form.Group>
            <div className="row mb-3">
              <div className="col-md-4">
                <Form.Label>Products to show</Form.Label>
                <Form.Select
                  value={content.featuredProducts?.limit || 9}
                  onChange={(e) =>
                    patch("featuredProducts", {
                      ...content.featuredProducts,
                      limit: Number(e.target.value),
                    })
                  }
                >
                  {[6, 8, 9, 12].map((n) => (
                    <option key={n} value={n}>
                      {n} products
                    </option>
                  ))}
                </Form.Select>
              </div>
              <div className="col-md-4">
                <Form.Label>Button text</Form.Label>
                <Form.Control
                  value={content.featuredProducts?.buttonText || ""}
                  onChange={(e) =>
                    patch("featuredProducts", {
                      ...content.featuredProducts,
                      buttonText: e.target.value,
                    })
                  }
                />
              </div>
              <div className="col-md-4">
                <Form.Label>Button link</Form.Label>
                <Form.Control
                  value={content.featuredProducts?.buttonLink || ""}
                  onChange={(e) =>
                    patch("featuredProducts", {
                      ...content.featuredProducts,
                      buttonLink: e.target.value,
                    })
                  }
                />
              </div>
            </div>

            <FeaturedProductPicker
              selectedIds={content.featuredProducts?.productIds || []}
              limit={content.featuredProducts?.limit || 9}
              onChange={(productIds) =>
                patch("featuredProducts", {
                  ...content.featuredProducts,
                  productIds,
                })
              }
            />

            <div className="home-cms-actions">
              <Button
                variant="dark"
                disabled={saving}
                onClick={() => saveSection("featuredProducts")}
              >
                {saving ? <Spinner size="sm" animation="border" /> : <FaSave />}{" "}
                Save featured products
              </Button>
            </div>
          </>
        )}

        {activeTab === "carousel" && (
          <>
            <Form.Group className="mb-3">
              <Form.Label>Slide interval (ms)</Form.Label>
              <Form.Control
                type="number"
                min={1000}
                max={30000}
                value={content.carousel.intervalMs || 3000}
                onChange={(e) =>
                  patch("carousel", {
                    ...content.carousel,
                    intervalMs: Number(e.target.value),
                  })
                }
              />
            </Form.Group>

            {(content.carousel.items || []).map((item, index) => (
              <div className="home-cms-item" key={index}>
                <div className="home-cms-item-head">
                  <h4>Slide {index + 1}</h4>
                  <Button
                    size="sm"
                    variant="outline-danger"
                    onClick={() => removeItem("carousel", index)}
                  >
                    <FaTrash />
                  </Button>
                </div>
                <CmsImageField
                  label="Slide image"
                  value={item.image}
                  onChange={(v) => updateItem("carousel", index, "image", v)}
                  onPendingFile={(file) =>
                    setPendingFile(`carousel.items.${index}.image`, file)
                  }
                />
                <div className="row">
                  <div className="col-md-6 mb-2">
                    <Form.Label>Button text</Form.Label>
                    <Form.Control
                      value={item.buttonText || ""}
                      onChange={(e) =>
                        updateItem("carousel", index, "buttonText", e.target.value)
                      }
                    />
                  </div>
                  <div className="col-md-6 mb-2">
                    <Form.Label>Button link</Form.Label>
                    <Form.Control
                      value={item.link || ""}
                      onChange={(e) =>
                        updateItem("carousel", index, "link", e.target.value)
                      }
                    />
                  </div>
                </div>
              </div>
            ))}

            <Button
              variant="outline-dark"
              className="mb-3"
              onClick={() => addItem("carousel", emptyCarouselItem)}
            >
              <FaPlus /> Add slide
            </Button>

            <div className="home-cms-actions">
              <Button variant="dark" disabled={saving} onClick={() => saveSection("carousel")}>
                {saving ? <Spinner size="sm" animation="border" /> : <FaSave />} Save carousel
              </Button>
            </div>
          </>
        )}

        {activeTab === "industries" && (
          <>
            <div className="row mb-3">
              <div className="col-md-4">
                <Form.Label>Subtitle</Form.Label>
                <Form.Control
                  value={content.industries.subtitle || ""}
                  onChange={(e) =>
                    patch("industries", {
                      ...content.industries,
                      subtitle: e.target.value,
                    })
                  }
                />
              </div>
              <div className="col-md-8">
                <Form.Label>Title</Form.Label>
                <Form.Control
                  value={content.industries.title || ""}
                  onChange={(e) =>
                    patch("industries", {
                      ...content.industries,
                      title: e.target.value,
                    })
                  }
                />
              </div>
            </div>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={content.industries.description || ""}
                onChange={(e) =>
                  patch("industries", {
                    ...content.industries,
                    description: e.target.value,
                  })
                }
              />
            </Form.Group>

            {(content.industries.items || []).map((item, index) => (
              <div className="home-cms-item" key={index}>
                <div className="home-cms-item-head">
                  <h4>Industry {index + 1}</h4>
                  <Button
                    size="sm"
                    variant="outline-danger"
                    onClick={() => removeItem("industries", index)}
                  >
                    <FaTrash />
                  </Button>
                </div>
                <div className="row">
                  <div className="col-md-6 mb-2">
                    <Form.Label>Title</Form.Label>
                    <Form.Control
                      value={item.title || ""}
                      onChange={(e) =>
                        updateItem("industries", index, "title", e.target.value)
                      }
                    />
                  </div>
                  <div className="col-12 mb-2">
                    <Form.Label>Details</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={item.description || ""}
                      onChange={(e) =>
                        updateItem("industries", index, "description", e.target.value)
                      }
                    />
                  </div>
                  <div className="col-12">
                    <CmsImageField
                      label="Image"
                      value={item.image}
                      onChange={(v) => updateItem("industries", index, "image", v)}
                      onPendingFile={(file) =>
                        setPendingFile(`industries.items.${index}.image`, file)
                      }
                    />
                  </div>
                </div>
              </div>
            ))}

            <Button
              variant="outline-dark"
              className="mb-3"
              onClick={() => addItem("industries", emptyIndustryItem)}
            >
              <FaPlus /> Add industry
            </Button>

            <div className="home-cms-actions">
              <Button variant="dark" disabled={saving} onClick={() => saveSection("industries")}>
                {saving ? <Spinner size="sm" animation="border" /> : <FaSave />} Save industries
              </Button>
            </div>
          </>
        )}

        {activeTab === "brandPartners" && (
          <>
            <div className="row mb-3">
              <div className="col-md-4">
                <Form.Label>Subtitle</Form.Label>
                <Form.Control
                  value={content.brandPartners.subtitle || ""}
                  onChange={(e) =>
                    patch("brandPartners", {
                      ...content.brandPartners,
                      subtitle: e.target.value,
                    })
                  }
                />
              </div>
              <div className="col-md-8">
                <Form.Label>Title</Form.Label>
                <Form.Control
                  value={content.brandPartners.title || ""}
                  onChange={(e) =>
                    patch("brandPartners", {
                      ...content.brandPartners,
                      title: e.target.value,
                    })
                  }
                />
              </div>
            </div>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={content.brandPartners.description || ""}
                onChange={(e) =>
                  patch("brandPartners", {
                    ...content.brandPartners,
                    description: e.target.value,
                  })
                }
              />
            </Form.Group>

            {(content.brandPartners.items || []).map((item, index) => (
              <div className="home-cms-item" key={index}>
                <div className="home-cms-item-head">
                  <h4>Brand {index + 1}</h4>
                  <Button
                    size="sm"
                    variant="outline-danger"
                    onClick={() => removeItem("brandPartners", index)}
                  >
                    <FaTrash />
                  </Button>
                </div>
                <CmsImageField
                  label="Logo image"
                  value={item.image}
                  onChange={(v) => updateItem("brandPartners", index, "image", v)}
                  onPendingFile={(file) =>
                    setPendingFile(`brandPartners.items.${index}.image`, file)
                  }
                />
                <div className="row">
                  <div className="col-md-6 mb-2">
                    <Form.Label>Alt text</Form.Label>
                    <Form.Control
                      value={item.alt || ""}
                      onChange={(e) =>
                        updateItem("brandPartners", index, "alt", e.target.value)
                      }
                    />
                  </div>
                  <div className="col-md-6 mb-2">
                    <Form.Label>Link (optional)</Form.Label>
                    <Form.Control
                      value={item.link || ""}
                      onChange={(e) =>
                        updateItem("brandPartners", index, "link", e.target.value)
                      }
                      placeholder="https://..."
                    />
                  </div>
                </div>
              </div>
            ))}

            <Button
              variant="outline-dark"
              className="mb-3"
              onClick={() => addItem("brandPartners", emptyBrandItem)}
            >
              <FaPlus /> Add brand logo
            </Button>

            <div className="home-cms-actions">
              <Button
                variant="dark"
                disabled={saving}
                onClick={() => saveSection("brandPartners")}
              >
                {saving ? <Spinner size="sm" animation="border" /> : <FaSave />} Save brands
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminHomeContent;
